import { orderBy, shuffle } from 'lodash';
import { Injectable } from '@angular/core';
import { Action, Selector, State, Store, type StateContext } from '@ngxs/store';
import { Navigate, RouterState } from '@ngxs/router-plugin';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatActions from '@medsurf/flat-actions';
import { MatomoControlService, ApiControlService } from '@medsurf/flat-services';
import * as FlatStates from '../../internal';

export interface TrainingSharedStateModel {
  // Loaded Slides
  typedAvailableBlocks: { [id: string]: FlatModels.TrainingSharedModels.AvailableBlock[] };
  typedAvailableSlides: { [id: string]: FlatModels.TrainingSharedModels.AvailableSlide[] };
  // Current Slides
  selectedStartUrl: string | null,
  selectedNodes: string[];
  typedSelectedSlideResults: { [id: string]: FlatModels.TrainingSharedModels.TrainingResultType[] };
  // Choices
  typedQuestionChoicesOrder: { [id: string]: string[] };
}

@State<TrainingSharedStateModel>({
  name: 'trainingShared',
  defaults: {
    // Loaded Trainings
    typedAvailableBlocks: {},
    typedAvailableSlides: {},
    // Current Training
    selectedStartUrl: null,
    selectedNodes: [],
    typedSelectedSlideResults: {},
    // Choices
    typedQuestionChoicesOrder: {},
  }
})
@Injectable()
export class TrainingSharedState {
  constructor(public store: Store,
              public apiControlService: ApiControlService,
              public matomoControlService: MatomoControlService) {
  }

  //<editor-fold desc="Selectors">

  @Selector([TrainingSharedState])
  static typedAvailableBlocks$(state: TrainingSharedStateModel): { [id: string]: FlatModels.TrainingSharedModels.AvailableBlock[] } {
    return state.typedAvailableBlocks;
  }

  @Selector([
    TrainingSharedState.typedAvailableBlocks$,
    FlatStates.NodeEntityState.currentSelectedSubjectNode$
  ])
  static currentAvailableBlocks$(typedAvailableBlocks: { [id: string]: FlatModels.TrainingSharedModels.AvailableBlock[] },
                                 subject: FlatModels.NodeEntityModels.SubjectNode) {
    return subject && typedAvailableBlocks && typedAvailableBlocks[subject.id] !== undefined ? typedAvailableBlocks[subject.id] : null;
  }

  @Selector([TrainingSharedState.currentAvailableBlocks$])
  static currentAvailableBlocksByParentId$(currentAvailableBlocks: FlatModels.TrainingSharedModels.AvailableBlock[]) {
    return (parentId: string) => {
      return currentAvailableBlocks ? currentAvailableBlocks.filter(e => e.parentId === parentId) : [];
    }
  }

  @Selector([TrainingSharedState])
  static typedAvailableSlides$(state: TrainingSharedStateModel): { [id: string]: FlatModels.TrainingSharedModels.AvailableSlide[] } {
    return state.typedAvailableSlides;
  }

  @Selector([
    TrainingSharedState.typedAvailableSlides$,
    FlatStates.NodeEntityState.currentSelectedSubjectNode$
  ])
  static currentAvailableSlides$(typedAvailableSlides: { [id: string]: FlatModels.TrainingSharedModels.AvailableSlide[] },
                                 subject: FlatModels.NodeEntityModels.SubjectNode) {
    return subject && typedAvailableSlides && typedAvailableSlides[subject.id] !== undefined ? typedAvailableSlides[subject.id] : null;
  }

  @Selector([
    TrainingSharedState.selectedNodes$,
    FlatStates.NodeEntityState.typedEntities$
  ])
  static selectedNodesDeduped$(selectedNodes: string[], typedEntities: ReturnType<typeof FlatStates.NodeEntityState.typedEntities$>) {
    const pageSet = new Set<string>();
    return selectedNodes.filter((id) => {
      const node = typedEntities[id];
      if (!node) return false;
      if (pageSet.has(node.page)) return false;
      pageSet.add(node.page);
      return true;
    })
  }

  @Selector([
    FlatStates.PageEntityState.typedEntities$,
    FlatStates.NodeEntityState.typedEntities$,
    TrainingSharedState.currentAvailableSlides$,
    TrainingSharedState.currentAvailableBlocks$,
  ])
  static quizStartData$(
    typedPages: ReturnType<typeof FlatStates.PageEntityState.typedEntities$>,
    typedNodes: ReturnType<typeof FlatStates.NodeEntityState.typedEntities$>,
    currentAvailableSlides: ReturnType<typeof TrainingSharedState.currentAvailableSlides$>,
    currentAvailableBlocks: ReturnType<typeof TrainingSharedState.currentAvailableBlocks$>
  ) {

    // Combine slides and blocks
    const currentAvailableNodes = [...(currentAvailableSlides ?? []), ...(currentAvailableBlocks ?? [])];

    // Map page entity and position to available nodes
    const loadedCurrentAvailableNodes = currentAvailableNodes?.map(item => {
      const node = typedNodes[item.id];
      const page = typedPages[node.page];
      if (!node || !page) return null;
      return { 
        id: item.id, 
        parentId: item.parentId, 
        position: node.position,
        page, 
        children: [] as FlatModels.TrainingSharedModels.QuizStartDataItem[], 
        expanded: false
      }
    }).filter(item => item !== null);

    const loadedCurrentAvailableNodesMap = new Map(loadedCurrentAvailableNodes.map(item => [item.id, item]));
    
    // Loop through available nodes and add children
    loadedCurrentAvailableNodes.forEach(item => {
      if (item.parentId) {
        const parent = loadedCurrentAvailableNodesMap.get(item.parentId);
        if (parent) {
          parent.children.push(item);
        } else {
          const node = typedNodes[item.parentId];
          const page = typedPages[node?.page];
          if (!node || !page || !node.parentId) return;
          loadedCurrentAvailableNodesMap.set(item.parentId, {
            id: item.parentId,
            parentId: '',
            position: node.position,
            page,
            children: [item],
            expanded: false,
          })
        }
      }
    })

    // Get chapter nodes
    const items = Array.from(loadedCurrentAvailableNodesMap.values()).filter(item => item.parentId === '');

    // Accumulate node ids from children slides
    const itemsWithNodeIds = items.map(item => {
      const children = item.children.map(child => {
        const nodeIds = child.children.map(c => c.id);
        return {...child, nodeIds }
      })
      const nodeIds = children.map(c => c.nodeIds).flat()
      return { ...item, children, nodeIds }
    })

    return {
      nodeIds: itemsWithNodeIds.map(item => item.nodeIds).flat(),
      items: itemsWithNodeIds,
    }
  }

  @Selector([TrainingSharedState])
  static selectedStartUrl$(state: TrainingSharedStateModel) {
    return state.selectedStartUrl;
  }

  @Selector([TrainingSharedState])
  static selectedNodes$(state: TrainingSharedStateModel) {
    return state.selectedNodes;
  }

  @Selector([TrainingSharedState.selectedNodes$])
  static selectedNodesSet$(selectedNodes: string[]) {
    return new Set(selectedNodes);
  }

  @Selector([
    TrainingSharedState.selectedNodes$,
    FlatStates.NodeEntityState.typedEntities$
  ])
  static typedSelectedNodes$(selectedNodes: string[],
                             typedNodes: { [id: string]: FlatModels.NodeEntityModels.NodeEntityType }) {
    if (selectedNodes.length !== 0) {
      return selectedNodes.reduce((arr, id) => {
        const entity = typedNodes[id];
        if (entity) {
          arr[entity.id] = entity;
        }
        return arr;
      }, {} as { [id: string]: FlatModels.NodeEntityModels.NodeEntityType });
    }
    return {};
  }

  @Selector([TrainingSharedState.typedSelectedNodes$])
  static selectedSlideNodes$(typedSelectedNodes: { [id: string]: FlatModels.NodeEntityModels.NodeEntityType }) {
    return Object.values(typedSelectedNodes);
  }
  

  @Selector([
    TrainingSharedState.selectedSlideNodes$,
    FlatStates.PageEntityState.typedEntities$
  ])
  static selectedSlidePages$(selectedSlideNodes: FlatModels.NodeEntityModels.NodeEntityType[],
                             typedEntities: { [id: string]: FlatModels.PageEntityModels.PageEntityType }): FlatModels.PageEntityModels.Training[] {
    if (selectedSlideNodes) {
      return selectedSlideNodes.reduce((arr, node) => {
        const entity = typedEntities[node.page as unknown as string] as FlatModels.PageEntityModels.Training;
        if (entity) {
          arr.push(entity);
        }
        return arr;
      }, [] as FlatModels.PageEntityModels.Training[]);
    }
    return [];
  }
  
  @Selector([TrainingSharedState.selectedSlidePages$])
  static selectedSlidePageSet$(selectedSlidePages: FlatModels.PageEntityModels.PageEntityType[]) {
    return new Set<string>(selectedSlidePages.map(ssp => ssp.id));
  }

  @Selector([
    TrainingSharedState.selectedNodes$,
    FlatStates.NodeEntityState.currentSelectedSlideNode$
  ])
  static selectedNodeIndex$(selectedNodes: string[],
                            currentSelectedSlideNode: FlatModels.NodeEntityModels.SlideNode) {
    if (currentSelectedSlideNode && selectedNodes.length !== 0) {
      const index = selectedNodes.findIndex(ss => ss === currentSelectedSlideNode.id);
      if (index > -1) {
        return index;
      }
    }
    return null;
  }

  @Selector([
    TrainingSharedState.typedSelectedSlideResults$, 
    TrainingSharedState.selectedNodes$,
    TrainingSharedState.selectedSlideNodes$
  ])
  static selectedQuestionIndex$(typedSelectedSlideResults: { [id: string]: FlatModels.TrainingSharedModels.TrainingResultType[] },
                                selectedNodes: string[],
                                selectedSlideNodes: FlatModels.NodeEntityModels.NodeEntityType[]) {
    let index = 0;
    const questionMap: { [key: string]: number } = {};

    selectedNodes.forEach(id => {
      const node = selectedSlideNodes.find(n => n.id === id);
      if (!node) return;
      const trainingResults = [...(typedSelectedSlideResults[node.page as unknown as string] || [])];
      trainingResults.sort((a, b) => a.order - b.order);
      trainingResults.forEach((trainingResult) => {
        questionMap[trainingResult.questionId] = index;
        index++;
      })
    })
    
    return (questionId: string) => {
      return Object.hasOwn(questionMap, questionId) ? questionMap[questionId] : null;
    }; 
  }

  @Selector([
    TrainingSharedState.selectedNodeIndex$,
    TrainingSharedState.selectedNodes$
  ])
  static selectedSlidePercentage$(selectedNodeIndex: number | null,
                                  selectedNodes: string[]) {
    if (selectedNodeIndex !== null && selectedNodes.length !== 0) {
      const item = 1 / selectedNodes.length;
      return (item * (selectedNodeIndex + 1)) - item / 2;
    }
    return null;
  }

  @Selector([TrainingSharedState.selectedNodeIndex$])
  static previousNodeIndex$(selectedNodeIndex: number | null) {
    return selectedNodeIndex !== null && selectedNodeIndex > 0 ? (selectedNodeIndex - 1) : null;
  }

  @Selector([
    TrainingSharedState.previousNodeIndex$,
    TrainingSharedState.selectedNodes$,
    FlatStates.NodeEntityState.typedEntities$
  ])
  static previousSlideNode$(previousNodeIndex: number | null,
                            selectedNodes: string[],
                            typedEntities: { [id: string]: FlatModels.NodeEntityModels.NodeEntityType }) {
    if (previousNodeIndex !== null && selectedNodes.length !== 0) {
      return typedEntities[selectedNodes[previousNodeIndex]] || null;
    }
    return null;
  }

  @Selector([
    TrainingSharedState.previousSlidePage$,
    FlatStates.PageEntityState.typedEntities$
  ])
  static previousSlidePage$(previousSlideNode: FlatModels.NodeEntityModels.NodeEntityType,
                            typedEntities: { [id: string]: FlatModels.PageEntityModels.PageEntityType }) {
    if (previousSlideNode) {
      return typedEntities[previousSlideNode.page as unknown as string] || null;
    }
    return null;
  }

  @Selector([
    TrainingSharedState.previousSlideNode$,
    TrainingSharedState.selectedStartUrl$,
    FlatStates.NodeEntityState.typedRouteElements$
  ])
  static previousSlideUrl$(previousSlideNode: FlatModels.NodeEntityModels.SlideNode | null,
                           selectedQuizUrl: string | null,
                           typedRouteElements: { [id: string]: FlatModels.NodeEntityModels.RouteElement }) {
    if (previousSlideNode && previousSlideNode.parentId) {
      const routeElement = typedRouteElements[previousSlideNode.parentId];
      if (routeElement) {
        return 'quiz/' + routeElement.path + '/' + previousSlideNode.route;
      } else {
        throw new FlatModels.ErrorControlModels.MedsurfError(
          'notification.parent_node_not_found',
          FlatModels.ErrorControlModels.ErrorLevel.enum.error,
          FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
          "LIBS.FlatStates.TrainingSharedState.previousSlideUrl$"
        );
      }
    } else if (selectedQuizUrl) {
      return selectedQuizUrl;
    }
    return null;
  }

  @Selector([
    TrainingSharedState.selectedNodeIndex$,
    TrainingSharedState.selectedNodes$
  ])
  static nextNodeIndex$(selectedNodeIndex: number | null,
                        selectedNodes: string[]) {
    return selectedNodeIndex !== null && (selectedNodeIndex + 1) < selectedNodes.length ? (selectedNodeIndex + 1) : null;
  }

  @Selector([
    TrainingSharedState.nextNodeIndex$,
    TrainingSharedState.selectedNodes$,
    FlatStates.NodeEntityState.typedEntities$
  ])
  static nextSlideNode$(nextNodeIndex: number | null,
                        selectedNodes: string[],
                        typedEntities: { [id: string]: FlatModels.NodeEntityModels.NodeEntityType }) {
    if (nextNodeIndex !== null && selectedNodes.length !== 0) {
      const selectedNode = Array.from(selectedNodes)[nextNodeIndex];
      if (selectedNode) {
        return typedEntities[selectedNode] || null;
      }
    }
    return null;
  }

  @Selector([
    TrainingSharedState.nextSlideNode$,
    FlatStates.PageEntityState.typedEntities$
  ])
  static nextSlidePage$(nextSlideNode: FlatModels.NodeEntityModels.NodeEntityType,
                        typedEntities: { [id: string]: FlatModels.PageEntityModels.PageEntityType }) {
    if (nextSlideNode) {
      return typedEntities[nextSlideNode.page as unknown as string] || null;
    }
    return null;
  }
  
  @Selector([
    TrainingSharedState.nextSlideNode$,
    TrainingSharedState.selectedStartUrl$,
    FlatStates.NodeEntityState.typedRouteElements$,
    FlatStates.PageEntityState.currentSelectedBlockPage$,
    FlatStates.NavigationControlState.chapterUrl$,
  ])
  static nextSlideUrl$(nextSlideNode: FlatModels.NodeEntityModels.SlideNode | null,
                       selectedQuizUrl: string | null,
                       typedRouteElements: { [id: string]: FlatModels.NodeEntityModels.RouteElement },
                       blockPage: FlatModels.PageEntityModels.PageEntityType,
                       chapterUrl: string) {
    if (nextSlideNode && nextSlideNode.parentId) {
      const routeElement = typedRouteElements[nextSlideNode.parentId];
      if (routeElement) {
        return 'quiz/' + routeElement.path + '/' + nextSlideNode.route;
      } else {
        throw new FlatModels.ErrorControlModels.MedsurfError(
          'notification.parent_node_not_found',
          FlatModels.ErrorControlModels.ErrorLevel.enum.error,
          FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
          "LIBS.FlatStates.TrainingSharedState.nextSlideUrl$"
        );
      }
    } else if (selectedQuizUrl) {
      if (blockPage.type === FlatModels.PageEntityModels.PageType.enum.Case) {
        return chapterUrl;
      }
      return selectedQuizUrl + '/results';
    }
    return null;
  }

  @Selector([TrainingSharedState])
  static typedSelectedSlideResults$(state: TrainingSharedStateModel) {
    return state.typedSelectedSlideResults;
  }

  @Selector([TrainingSharedState.typedSelectedSlideResults$])
  static selectedSlideResults$(typedSelectedSlideResults: { [id: string]: FlatModels.TrainingSharedModels.TrainingResultType[] }) {
    return Object.values(typedSelectedSlideResults).reduce((acc, result) => {
      acc.push(...result);
      return acc;
    }, []);
  }

  @Selector([TrainingSharedState.selectedSlideResults$])
  static selectedTotalSlideResults$(selectedSlideResults: FlatModels.TrainingSharedModels.TrainingResultType[]) {
    if (Array.isArray(selectedSlideResults)) {
      return selectedSlideResults.reduce((arr, result) => {
        arr += result.total || 0;
        return arr;
      }, 0);
    }
    return 0;
  }

  @Selector([TrainingSharedState.selectedSlideResults$])
  static selectedEmptySlideResults$(selectedSlideResults: FlatModels.TrainingSharedModels.TrainingResultType[]) {
    if (Array.isArray(selectedSlideResults)) {
      return selectedSlideResults.reduce((arr, result) => {
        arr += result.empty || 0;
        return arr;
      }, 0);
    }
    return 0;
  }

  @Selector([
    TrainingSharedState.selectedTotalSlideResults$,
    TrainingSharedState.selectedEmptySlideResults$
  ])
  static selectedEmptyPercentageSlideResults$(selectedTotalSlideResults: number,
                                              selectedEmptySlideResults: number) {
    return Math.round(selectedEmptySlideResults / selectedTotalSlideResults * 1000) / 10;
  }

  @Selector([TrainingSharedState.selectedSlideResults$])
  static selectedCorrectSlideResults$(selectedSlideResults: FlatModels.TrainingSharedModels.TrainingResultType[]) {
    if (Array.isArray(selectedSlideResults)) {
      return selectedSlideResults.reduce((arr, result) => {
        arr += result.correct;
        return arr;
      }, 0);
    }
    return 0;
  }

  @Selector([
    TrainingSharedState.selectedTotalSlideResults$,
    TrainingSharedState.selectedCorrectSlideResults$
  ])
  static selectedCorrectPercentageSlideResults$(selectedTotalSlideResults: number,
                                                selectedCorrectSlideResults: number) {
    return Math.round(selectedCorrectSlideResults / selectedTotalSlideResults * 1000) / 10;
  }

  @Selector([TrainingSharedState.selectedSlideResults$])
  static selectedWrongSlideResults$(selectedSlideResults: FlatModels.TrainingSharedModels.TrainingResultType[]) {
    if (Array.isArray(selectedSlideResults)) {
      return selectedSlideResults.reduce((arr, result) => {
        arr += result.wrong;
        return arr;
      }, 0);
    }
    return 0;
  }

  @Selector([
    TrainingSharedState.selectedTotalSlideResults$,
    TrainingSharedState.selectedWrongSlideResults$
  ])
  static selectedWrongPercentageSlideResults$(selectedTotalSlideResults: number,
                                              selectedWrongSlideResults: number) {
    return Math.round(selectedWrongSlideResults / selectedTotalSlideResults * 1000) / 10;
  }

  @Selector([
    TrainingSharedState.typedSelectedSlideResults$,
    FlatStates.PageEntityState.currentSelectedSlidePage$
  ])
  static currentSlideResults$(typedSelectedSlideResults: { [id: string]: FlatModels.TrainingSharedModels.TrainingResultType[] },
                              currentSelectedSlidePage: FlatModels.PageEntityModels.Training) {
    return currentSelectedSlidePage && typedSelectedSlideResults && typedSelectedSlideResults[currentSelectedSlidePage.id] !== undefined ? typedSelectedSlideResults[currentSelectedSlidePage.id] : [];
  }

  @Selector([TrainingSharedState.currentSlideResults$])
  static currentSlideResultShowFeedback$(currentSlideResults: FlatModels.TrainingSharedModels.TrainingResultType[]) {
    return Array.isArray(currentSlideResults) ? currentSlideResults.every(ctr => ctr.showFeedback) : false;
  }

  @Selector([TrainingSharedState.currentSlideResults$])
  static currentSlideResultShowExplanation$(currentSlideResults: FlatModels.TrainingSharedModels.TrainingResultType[]) {
    return Array.isArray(currentSlideResults) ? currentSlideResults.every(ctr => ctr.showExplanation) : false;
  }

  @Selector([
    TrainingSharedState.currentSlideResults$,
    FlatStates.QuestionEntityState.typedEntities$,
    FlatStates.PageEntityState.currentSelectedSlidePage$
  ])
  static currentSlideResultByQuestionId$(currentSlideResults: FlatModels.TrainingSharedModels.TrainingResultType[],
                                         typedQuestions: {[id: string]: FlatModels.QuestionEntityModels.QuestionEntityType},
                                         currentSelectedSlidePage: FlatModels.PageEntityModels.Training) {
    return (questionId: string) => {
      let result = Array.isArray(currentSlideResults) && currentSlideResults.find(sr => sr.questionId === questionId) || null;
      if (!result) {
        result = typedQuestions?.[questionId] && currentSelectedSlidePage?.id ? TrainingSharedState._createQuestionResult(typedQuestions[questionId], currentSelectedSlidePage.id) : null;
      }

      // Invalidate Question Result if Question was changed
      const question = typedQuestions?.[questionId];
      const { MappingQuestion, KprimeQuestion, TrueFalseQuestion } = FlatModels.QuestionEntityModels.QuestionType.enum;
      
      if (result && question.type && (question.type === MappingQuestion || question.type === KprimeQuestion || question.type === TrueFalseQuestion)) {
        if ((question as FlatModels.QuestionEntityModels.KprimeQuestion).solutions?.length !== (result as FlatModels.TrainingSharedModels.KPrimQuestionResult).answer?.length) {
          result = typedQuestions?.[questionId] && currentSelectedSlidePage?.id ? TrainingSharedState._createQuestionResult(typedQuestions[questionId], currentSelectedSlidePage.id) : null;
        }
      }

      return result;
    };
  }

  @Selector([TrainingSharedState.typedSelectedSlideResults$])
  static selectedSlideResultsRequiredAnswered$(typedSelectedSlideResults: { [id: string]: FlatModels.TrainingSharedModels.TrainingResultType[] }) {
    const slideResultsRequiredAnswered: { [id: string]: boolean } = {};
    Object.entries(typedSelectedSlideResults).forEach(([id, trainingResults]) => {
      if (trainingResults.length === 0) {
        slideResultsRequiredAnswered[id] = true;
      } else {
        const requiredResults = trainingResults.filter(qr => qr.required == true);
        slideResultsRequiredAnswered[id] = requiredResults.every(r => r.showFeedback);
      }
    });
    return slideResultsRequiredAnswered;
  }

  @Selector([
    TrainingSharedState.selectedSlideResultsRequiredAnswered$,
    TrainingSharedState.selectedNodes$,
    TrainingSharedState.typedSelectedNodes$
  ])
  static slideSelectionAvailable$(slideResultsRequiredAnswered: { [id: string]: boolean },
                                  selectedNodes: string[],
                                  typedSelectedNodes: { [id: string]: FlatModels.NodeEntityModels.NodeEntityType }) {
    const slideResultsAvailable: { [id: string]: boolean } = {};
    let isAvailableSeriesBlock = true;
    let isAvailableSeriesBlockIndex = -1;
    for (let i = 0; i < selectedNodes.length; i++) {
      const slideId = Array.from(selectedNodes)[i];
      const slideNode = typedSelectedNodes[slideId];
      if (!slideNode) {
        return;
        /* TODO check this -> we should navigate to the quiz start here -> but it's a selector, not a action
        throw new FlatModels.ErrorControlModels.MedsurfError(
          'notification.node_not_found',
          FlatModels.ErrorControlModels.ErrorLevel.enum.error,
          FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
          "LIBS.FlatStates.TrainingSharedState.slideSelectionAvailable$"
        );
        */
      }
      const pageId = slideNode.page as unknown as string;
      if (isAvailableSeriesBlock) {
        const isAvailable = slideResultsRequiredAnswered[pageId] || false;
        if (!isAvailable) {
          slideResultsAvailable[pageId] = isAvailableSeriesBlockIndex === (i - 1);
          isAvailableSeriesBlock = false;
        } else {
          slideResultsAvailable[pageId] = isAvailable;
          isAvailableSeriesBlockIndex = i;
        }
      } else {
        if (!slideResultsAvailable[pageId]) {
          slideResultsAvailable[pageId] = false;
        }
      }
    }
    return slideResultsAvailable;
  }

  @Selector([
    TrainingSharedState.selectedSlidePageSet$,
    TrainingSharedState.typedSelectedSlideResults$,
  ])
  static isTrainingSetupCorrectly$(selectedSlidePageSet: Set<string>,
                                   typedSelectedSlideResults: { [id: string]: FlatModels.TrainingSharedModels.TrainingResultType}) {
    return selectedSlidePageSet.size > 0 && selectedSlidePageSet.size === Object.keys(typedSelectedSlideResults).length;
  }

  @Selector([
    FlatStates.PageEntityState.currentSelectedBlockPage$,
    FlatStates.NavigationControlState.subjectUrl$,
    FlatStates.NavigationControlState.chapterUrl$,
  ])
  static trainingSetupIncorrectlyUrl$(blockPage: FlatModels.PageEntityModels.PageEntityType,
                                      subjectUrl: string | null,
                                      chapterUrl: string | null) {
    if (blockPage.type === FlatModels.PageEntityModels.PageType.enum.Case) {
      return chapterUrl;
    } else {
      return subjectUrl + '/quiz';
    }
  }

  @Selector([FlatStates.NodeEntityState.typedRouteElements$])
  static slideUrlByNode$(typedRouteElements: { [id: string]: FlatModels.NodeEntityModels.RouteElement }) {
    return (slideNode: FlatModels.NodeEntityModels.SlideNode) => {
      if (slideNode && slideNode.parentId) {
        const routeElement = typedRouteElements[slideNode.parentId];
        if (routeElement) {
          return ['/quiz/' + routeElement.path + '/' + slideNode.route];
        } else {
          throw new FlatModels.ErrorControlModels.MedsurfError(
            'notification.parent_node_not_found',
            FlatModels.ErrorControlModels.ErrorLevel.enum.error,
            FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
            "LIBS.FlatStates.TrainingSharedState.slideUrlByNode$"
          );
        }
      }
      throw new FlatModels.ErrorControlModels.MedsurfError(
        'notification.node_not_found',
        FlatModels.ErrorControlModels.ErrorLevel.enum.error,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "LIBS.FlatStates.TrainingSharedState.slideUrlByNode$"
      );
    }
  }
  
  @Selector([TrainingSharedState])
  static typedQuestionChoicesOrder$(state: TrainingSharedStateModel) {
    return state.typedQuestionChoicesOrder;
  }

  //<editor-fold desc="Admin">

  @Selector([
    TrainingSharedState.currentAvailableSlides$,
    FlatStates.NodeEntityState.typedEntities$,
    FlatStates.PageEntityState.typedEntities$
  ])
  static currentAvailableSlidePagesByChapterId$(currentAvailableSlides: FlatModels.TrainingSharedModels.AvailableSlide[],
                                                typedNodes: { [id: string]: FlatModels.NodeEntityModels.NodeEntityType },
                                                typedPages: { [id: string]: FlatModels.PageEntityModels.PageEntityType }) {
    return (chapterId: string) => {
      const chapter = typedNodes[chapterId];
      if (chapter && currentAvailableSlides) {
        return orderBy(chapter.children.reduce((arr, id) => {
          const node = typedNodes[id];
          const availableSlides = currentAvailableSlides.filter(cas => cas.parentId === id);
          if (node && !node.hidden && availableSlides.length !== 0) {
            const page = typedPages[node.page];
            if (page) {
              arr.push({
                node,
                page,
                availableSlides
              });
            }
          }
          return arr;
        }, [] as any[]), (a) => a.node.position || 0, 'asc');
      }
      return [];
    }
  }

  //</editor-fold>

  //</editor-fold>

  //<editor-fold desc="Actions">

  @Action(FlatActions.TrainingSharedActions.GetTrainingNodesByCurrentSubject)
  getTrainingNodesByCurrentSubject() {
    const currentAvailableSlides = this.store.selectSnapshot(TrainingSharedState.currentAvailableSlides$);
    if (currentAvailableSlides === null) {
      const subjectNode = this.store.selectSnapshot(FlatStates.NodeEntityState.currentSelectedSubjectNode$);
      if (subjectNode) {
        return this.apiControlService.sendMessage(
          new FlatActions.TrainingSharedActions.GetTrainingNodesBySubjectIdRequest(subjectNode.id)
        );
      }
    }
    return null;
  }

  @Action(FlatActions.TrainingSharedActions.GetTrainingNodesBySelectedSlides)
  getTrainingNodesBySelectedSlides() {
    const selectedNodes = this.store.selectSnapshot(TrainingSharedState.selectedNodes$);
    if (selectedNodes.length > 0) {
      return this.apiControlService.sendMessage(
        new FlatActions.TrainingSharedActions.GetTrainingNodesByIdsRequest(Array.from(selectedNodes))
      );
    }
    return null;
  }

  @Action(FlatActions.TrainingSharedActions.GetSlideEntitySuccess)
  getSlideEntitySuccess({getState, patchState, dispatch}: StateContext<TrainingSharedStateModel>,
                        {entityQueryResult}: FlatActions.TrainingSharedActions.GetSlideEntitySuccess) {
    if (entityQueryResult.subjectId) {
      const availableSlides = structuredClone(getState().typedAvailableSlides);
      availableSlides[entityQueryResult.subjectId] = [...availableSlides[entityQueryResult.subjectId] || [], {id: entityQueryResult.entity.id, page: entityQueryResult.entity.page, parentId: entityQueryResult.entity.parentId as string}];
      patchState({
        typedAvailableSlides: availableSlides
      });
      dispatch(new FlatActions.NodeEntityActions.GetEntitySuccess(entityQueryResult));
    } else {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        'notification.id_empty',
        FlatModels.ErrorControlModels.ErrorLevel.enum.error,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "LIBS.FlatStates.TrainingSharedState.getSlideEntitySuccess"
      );
    }
  }

  @Action(FlatActions.TrainingSharedActions.GetSlideEntitiesSuccess)
  getSlideEntitiesSuccess({getState, patchState, dispatch}: StateContext<TrainingSharedStateModel>,
                          {entityQueryResults}: FlatActions.TrainingSharedActions.GetSlideEntitiesSuccess) {
    let noSubjectId = false;
    const availableSlides = Object.assign({}, getState().typedAvailableSlides);
    for (const entityQueryResult of entityQueryResults) {
      if (entityQueryResult.subjectId) {
        availableSlides[entityQueryResult.subjectId] = [...availableSlides[entityQueryResult.subjectId] || [], {id: entityQueryResult.entity.id, page: entityQueryResult.entity.page, parentId: entityQueryResult.entity.parentId as string}];
      } else {
        noSubjectId = true;
      }
    }
    if (noSubjectId) {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        'notification.id_empty',
        FlatModels.ErrorControlModels.ErrorLevel.enum.error,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "LIBS.FlatStates.TrainingSharedState.getSlideEntitiesSuccess"
      );
    }
    patchState({
      typedAvailableSlides: availableSlides
    });
    dispatch(new FlatActions.NodeEntityActions.GetEntitiesSuccess(entityQueryResults));
  }

  @Action(FlatActions.TrainingSharedActions.GetBlockEntitySuccess)
  getBlockEntitySuccess({getState, patchState, dispatch}: StateContext<TrainingSharedStateModel>,
                        {entityQueryResult}: FlatActions.TrainingSharedActions.GetBlockEntitySuccess) {
    if (entityQueryResult.subjectId) {
      const availableBlocks = Object.assign({}, getState().typedAvailableBlocks);
      availableBlocks[entityQueryResult.subjectId] = [...availableBlocks[entityQueryResult.subjectId] || [], {id: entityQueryResult.entity.id, parentId: entityQueryResult.entity.parentId as string}];
      patchState({
        typedAvailableBlocks: availableBlocks
      });
      dispatch(new FlatActions.NodeEntityActions.GetEntitySuccess(entityQueryResult));
    } else {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        'notification.id_empty',
        FlatModels.ErrorControlModels.ErrorLevel.enum.error,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "LIBS.FlatStates.TrainingSharedState.getBlockEntitySuccess"
      );
    }
  }

  @Action(FlatActions.TrainingSharedActions.GetBlockEntitiesSuccess)
  getBlockEntitiesSuccess({getState, patchState, dispatch}: StateContext<TrainingSharedStateModel>,
                          {entityQueryResults}: FlatActions.TrainingSharedActions.GetBlockEntitiesSuccess) {
    let noSubjectId = false;
    const availableBlocks = Object.assign({}, getState().typedAvailableBlocks);
    for (const entityQueryResult of entityQueryResults) {
      if (entityQueryResult.subjectId) {
        availableBlocks[entityQueryResult.subjectId] = [...availableBlocks[entityQueryResult.subjectId] || [], {id: entityQueryResult.entity.id, parentId: entityQueryResult.entity.parentId as string}];
      } else {
        noSubjectId = true;
      }
    }
    if (noSubjectId) {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        'notification.id_empty',
        FlatModels.ErrorControlModels.ErrorLevel.enum.error,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "LIBS.FlatStates.TrainingSharedState.getBlockEntitiesSuccess"
      );
    }
    patchState({
      typedAvailableBlocks: availableBlocks
    });
    dispatch(new FlatActions.NodeEntityActions.GetEntitiesSuccess(entityQueryResults));
  }

  @Action(FlatActions.TrainingSharedActions.ToggleTrainingSelection)
  toggleTrainingSelection({getState, patchState}: StateContext<TrainingSharedStateModel>,
                          {slides, override}: FlatActions.TrainingSharedActions.ToggleTrainingSelection) {
    const selecteNodesSet = new Set(getState().selectedNodes);
    for (const slideId of slides) {
      if (selecteNodesSet.has(slideId)) {
        if (!override) {
          selecteNodesSet.delete(slideId);
        }
      } else {
        if (override) {
          selecteNodesSet.add(slideId);
        }
      }
    }
    patchState({
      selectedNodes: Array.from(selecteNodesSet)
    });
  }

  @Action(FlatActions.TrainingSharedActions.SetupQuestionChoicesOrder)
  setupQuestionChoicesOrder({getState, patchState}: StateContext<TrainingSharedStateModel>,
                            {question}: FlatActions.TrainingSharedActions.SetupQuestionChoicesOrder) {
    // Get Data
    const typedSelectedChoicesOrder = getState().typedQuestionChoicesOrder;
    
    // Check If Order Already Exists
    if (!typedSelectedChoicesOrder[question.id]) {
      // Get Choices
      const typedChoices = this.store.selectSnapshot(FlatStates.ChoiceEntityState.typedEntities$);
      
      // Sort Choices By Order And Map Id
      let choices = question.choices.reduce((arr, id) => {
        const entity = typedChoices[id];
        if (entity) {
          arr.push(entity);
        }
        return arr;
      }, [] as FlatModels.ChoiceEntityModels.Choice[])
      .sort((a, b) => a.order - b.order)
      .map(c => c.id);
      
      // Check Choices Count
      if (choices.length !== question.choices.length) {
        return;
      }
      
      // Check For Randomised Choices
      if (question.randomised) {
        choices = shuffle(choices);
      }
      
      // Add Choices
      typedSelectedChoicesOrder[question.id] = choices
      
      // Patch State
      patchState({
        typedQuestionChoicesOrder: typedSelectedChoicesOrder
      });
    }
  }

  @Action(FlatActions.TrainingSharedActions.Reset)
  reset({getState, patchState}: StateContext<TrainingSharedStateModel>) {
    // Get current typed question choices Order
    const typedQuestionChoicesOrder = structuredClone(getState().typedQuestionChoicesOrder);
    
    // Get question
    const typedQuestions = this.store.selectSnapshot(FlatStates.QuestionEntityState.typedEntities$);
    const questions = Object.keys(typedQuestionChoicesOrder).reduce<FlatModels.QuestionEntityModels.QuestionEntityType[]>((arr, questionId) => {
      const entity = typedQuestions[questionId];
      if (entity) {
        arr.push(entity);
      }
      return arr;
    }, []);
    
    patchState({
      selectedStartUrl: null,
      selectedNodes: [],
      typedSelectedSlideResults: {},
      typedQuestionChoicesOrder: {}
    });
    for (const question of questions) {
      this.store.dispatch(new FlatActions.TrainingSharedActions.SetupQuestionChoicesOrder(question));
    }
  }

  @Action(FlatActions.TrainingSharedActions.SetupTraining)
  setupTraining({getState, patchState, dispatch}: StateContext<TrainingSharedStateModel>) {
    // Shuffle Selected Nodes
    const selectedNodes = this.store.selectSnapshot(TrainingSharedState.selectedNodesDeduped$)
    const shuffledSelectedNodes = [...shuffle(selectedNodes)];

    // Get Questions
    const questions = this.store.selectSnapshot(FlatStates.QuestionEntityState.typedEntities$);

    // Setup Training Results
    const selectedTrainingPages = this.store.selectSnapshot(TrainingSharedState.selectedSlidePages$);
    const selectedTrainingResults: { [id: string]: FlatModels.TrainingSharedModels.TrainingResultType[] } = {};
    selectedTrainingPages.forEach(tp => {
      selectedTrainingResults[tp.id] = [];
      (tp.questions || []).forEach((q) => {
        let total = 0;
        let answer = undefined;
        let solutions: string[] | null = [];
        const question = questions[q as unknown as string];
        if (question) {
          switch (question.type) {
            case FlatModels.QuestionEntityModels.QuestionType.enum.MappingQuestion:
            case FlatModels.QuestionEntityModels.QuestionType.enum.KprimeQuestion:
            case FlatModels.QuestionEntityModels.QuestionType.enum.TrueFalseQuestion:
              // Cast
              solutions = (question as FlatModels.QuestionEntityModels.MappingQuestion | FlatModels.QuestionEntityModels.KprimeQuestion
                | FlatModels.QuestionEntityModels.TrueFalseQuestion).solutions;

              // Setup
              if (solutions) {
                total = solutions.length || 0;
                answer = solutions.map(() => '');
              }
              break;
            case FlatModels.QuestionEntityModels.QuestionType.enum.IndicationQuestion:
            case FlatModels.QuestionEntityModels.QuestionType.enum.LongListQuestion:
            case FlatModels.QuestionEntityModels.QuestionType.enum.SingleChoiceQuestion:
            case FlatModels.QuestionEntityModels.QuestionType.enum.FreeTextQuestion:
              total = 1;
              answer = null;
              break;
            default:
              throw new FlatModels.ErrorControlModels.MedsurfError(
                'notification.type_not_supported',
                FlatModels.ErrorControlModels.ErrorLevel.enum.error,
                FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
                "LIBS.FlatStates.TrainingSharedState.setupTraining"
              );
              // MULTIPLE_CHOICE = 'MultipleChoiceQuestion',
          }
          selectedTrainingResults[tp.id].push({
            trainingId: tp.id,
            questionId: question.id,
            required: question.required || false,
            order: question.order,
            answer,
            showFeedback: false,
            showExplanation: false,
            total,
            empty: total,
            correct: 0,
            wrong: 0,
          });
        }
      });
    });

    const routerState = this.store.selectSnapshot(RouterState);

    // Setup Training
    patchState({
      selectedStartUrl: routerState.state.url,
      selectedNodes: shuffledSelectedNodes,
      typedSelectedSlideResults: selectedTrainingResults
    });

    // Start Training
    dispatch(new FlatActions.TrainingSharedActions.Start());
  }

  @Action(FlatActions.TrainingSharedActions.SetupCase)
  setupCase({patchState, dispatch}: StateContext<TrainingSharedStateModel>) {
    // Get Selected Slides
    const currentBlockNode = this.store.selectSnapshot(FlatStates.NodeEntityState.currentSelectedBlockNode$);
    const selectedTrainings = currentBlockNode ? currentBlockNode.children || [] : [];

    // Get Questions
    const questions = this.store.selectSnapshot(FlatStates.QuestionEntityState.typedEntities$);

    // Set Selected Slides
    patchState({
      selectedNodes: selectedTrainings,
    });

    // Setup Case Results
    const currentSelectedCasePages = this.store.selectSnapshot(TrainingSharedState.selectedSlidePages$);
    const selectedTrainingResults: { [id: string]: FlatModels.TrainingSharedModels.TrainingResultType[] } = {};
    currentSelectedCasePages.forEach(tp => {
      selectedTrainingResults[tp.id] = [];
       (tp.questions || []).forEach((q) => {
        const question = questions[q as unknown as string];
        if (question) {
          const result = TrainingSharedState._createQuestionResult(question, tp.id);
          selectedTrainingResults[tp.id].push(result);
        }
      });
    });

    const routerState = this.store.selectSnapshot(RouterState);

    // Setup Case
    patchState({
      selectedStartUrl: routerState.state.url,
      typedSelectedSlideResults: selectedTrainingResults
    });

    // Start Case
    dispatch(new FlatActions.TrainingSharedActions.Start());
  }

  @Action(FlatActions.TrainingSharedActions.Start)
  start({getState, dispatch}: StateContext<TrainingSharedStateModel>) {
    const selectedNodes = getState().selectedNodes;
    const typedSelectedNodes = this.store.selectSnapshot(TrainingSharedState.typedSelectedNodes$);
    if (typedSelectedNodes) {
      const selectedNode = typedSelectedNodes[selectedNodes[0]];
      if (selectedNode) {
        const treeElements = this.store.selectSnapshot(FlatStates.NodeEntityState.routeElements$);
        const treeElement = treeElements.find(te => te.id === selectedNode.parentId);
        if (treeElement) {
          // Matomo Track Quiz / Case Start
          this.matomoControlService.trackQuizStart(getState().selectedNodes);

          // Navigate to first slide
          dispatch(new Navigate(['quiz/' + treeElement.path + '/' + selectedNode.route], {}, { replaceUrl: true }));
        } else {
          throw new FlatModels.ErrorControlModels.MedsurfError(
            'notification.parent_node_not_found',
            FlatModels.ErrorControlModels.ErrorLevel.enum.error,
            FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
            "LIBS.FlatStates.TrainingSharedState.start"
          );
        }
      } else {
        throw new FlatModels.ErrorControlModels.MedsurfError(
          'notification.node_not_found',
          FlatModels.ErrorControlModels.ErrorLevel.enum.error,
          FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
          "LIBS.FlatStates.TrainingSharedState.start"
        );
      }
    } else {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        'notification.no_slides_found',
        FlatModels.ErrorControlModels.ErrorLevel.enum.error,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "LIBS.FlatStates.TrainingSharedState.start"
      );
    }
  }

  @Action(FlatActions.TrainingSharedActions.UpdateSlideResult)
  updateSlideResult({getState, patchState}: StateContext<TrainingSharedStateModel>,
                    {slideResult}: FlatActions.TrainingSharedActions.UpdateSlideResult) {
    const typedSelectedSlideResults = getState().typedSelectedSlideResults;
    const trainingResult = typedSelectedSlideResults[slideResult.trainingId] || [slideResult];
    const index = trainingResult.findIndex(r => r.questionId === slideResult.questionId);
    const updatedTrainingResult = [...trainingResult];
    if (index !== -1) {
      updatedTrainingResult[index] = slideResult;
    } else {
      updatedTrainingResult.push(slideResult);
    }

    patchState({
      typedSelectedSlideResults: {
        ...typedSelectedSlideResults,
        [slideResult.trainingId]: updatedTrainingResult
      }
    });
  }
  
  @Action(FlatActions.TrainingSharedActions.SetShowFeedback)
  setShowFeedback({dispatch}: StateContext<TrainingSharedStateModel>,
                  {slideResult, question}: FlatActions.TrainingSharedActions.SetShowFeedback) {
    slideResult.showFeedback = true;

    // Results
    let solutions: string[] | null = [];
    let solution: number | null = null;
    switch (question.type) {
      case FlatModels.QuestionEntityModels.QuestionType.enum.MappingQuestion:
      case FlatModels.QuestionEntityModels.QuestionType.enum.KprimeQuestion:
      case FlatModels.QuestionEntityModels.QuestionType.enum.TrueFalseQuestion:
        // Cast
        solutions = (question as FlatModels.QuestionEntityModels.MappingQuestion
          | FlatModels.QuestionEntityModels.KprimeQuestion | FlatModels.QuestionEntityModels.TrueFalseQuestion).solutions;

        // Setup Results
        if (solutions) {
          slideResult.empty = solutions.filter((s, i) => Array.isArray(slideResult.answer) && slideResult.answer[i] === '').length;
          slideResult.correct = solutions.filter((s, i) => Array.isArray(slideResult.answer) && slideResult.answer[i] === s).length;
          slideResult.wrong = (slideResult.total || 0) - slideResult.empty - slideResult.correct;
        }
        break;
      case FlatModels.QuestionEntityModels.QuestionType.enum.IndicationQuestion:
      case FlatModels.QuestionEntityModels.QuestionType.enum.LongListQuestion:
      case FlatModels.QuestionEntityModels.QuestionType.enum.SingleChoiceQuestion:
        // Cast
        solution = (question as FlatModels.QuestionEntityModels.IndicationQuestion
          | FlatModels.QuestionEntityModels.LongListQuestion | FlatModels.QuestionEntityModels.SingleChoiceQuestion).solution;

        // Setup Results
        slideResult.empty = (slideResult.answer === null) ? 1 : 0;
        slideResult.correct = (slideResult.answer === solution) ? 1 : 0;
        slideResult.wrong = (slideResult.empty !== 1 && slideResult.correct !== 1) ? 1 : 0;
        break;
      case FlatModels.QuestionEntityModels.QuestionType.enum.FreeTextQuestion:
        break;
      default:
        throw new FlatModels.ErrorControlModels.MedsurfError(
          'notification.type_not_supported',
          FlatModels.ErrorControlModels.ErrorLevel.enum.error,
          FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
          "LIBS.FlatStates.TrainingSharedState.setShowFeedback"
        );
        // MULTIPLE_CHOICE = 'MultipleChoiceQuestion',
    }
    dispatch(new FlatActions.TrainingSharedActions.UpdateSlideResult(slideResult));
  }

  @Action(FlatActions.TrainingSharedActions.ToggleShowExplanation)
  toggleShowExplanation({dispatch}: StateContext<TrainingSharedStateModel>,
                        {slideResult}: FlatActions.TrainingSharedActions.ToggleShowExplanation) {
    slideResult.showExplanation = !slideResult.showExplanation;
    dispatch(new FlatActions.TrainingSharedActions.UpdateSlideResult(slideResult));
  }

  @Action(FlatActions.TrainingSharedActions.GoToStart)
  goToStart({getState, dispatch}: StateContext<TrainingSharedStateModel>) {
    const startUrl = getState().selectedStartUrl;
    if (startUrl) {
      dispatch(new Navigate([startUrl]));
    }
  }

  @Action(FlatActions.TrainingSharedActions.GoToSlideByNode)
  goToSlideByNode({dispatch}: StateContext<TrainingSharedStateModel>,
                  {slideNode}: FlatActions.TrainingSharedActions.GoToSlideByNode) {
    if (slideNode && slideNode.parentId) {
      const typedRouteElements = this.store.selectSnapshot(FlatStates.NodeEntityState.typedRouteElements$);
      const routeElement = typedRouteElements[slideNode.parentId];
      if (routeElement) {
        // Navigate to first slide
        dispatch(new Navigate(['quiz/' + routeElement.path + '/' + slideNode.route]));
      } else {
        throw new FlatModels.ErrorControlModels.MedsurfError(
          'notification.parent_node_not_found',
          FlatModels.ErrorControlModels.ErrorLevel.enum.error,
          FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
          "LIBS.FlatStates.TrainingSharedState.goToSlideByNode"
        );
      }
    } else {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        'notification.node_not_found',
        FlatModels.ErrorControlModels.ErrorLevel.enum.error,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "LIBS.FlatStates.TrainingSharedState.goToSlideByNode"
      );
    }
  }

  @Action(FlatActions.TrainingSharedActions.TrackQuizAnswer)
  trackQuizAnswer(state: StateContext<TrainingSharedStateModel>,
                  {slideId, question, isCorrect}: FlatActions.TrainingSharedActions.TrackQuizAnswer) {
    // Matomo
    this.matomoControlService.trackQuizAnswer(slideId, question, isCorrect);
  }

  //</editor-fold>

  //<editor-fold desc="Helper">

  protected static _createQuestionResult(question: FlatModels.QuestionEntityModels.QuestionEntityType, slideId: string): FlatModels.TrainingSharedModels.TrainingResultType {
    let total = 0;
    let answer = undefined;
    let solutions: string[] | undefined = [];
    switch (question.type) {
      case FlatModels.QuestionEntityModels.QuestionType.enum.MappingQuestion:
      case FlatModels.QuestionEntityModels.QuestionType.enum.KprimeQuestion:
      case FlatModels.QuestionEntityModels.QuestionType.enum.TrueFalseQuestion:
        // Cast
        solutions = (question as FlatModels.QuestionEntityModels.MappingQuestion | FlatModels.QuestionEntityModels.KprimeQuestion
            | FlatModels.QuestionEntityModels.TrueFalseQuestion).solutions;

        // Setup
        if (solutions) {
          total = solutions.length || 0;
          answer = solutions.map(() => '');
        }
        break;
      case FlatModels.QuestionEntityModels.QuestionType.enum.IndicationQuestion:
      case FlatModels.QuestionEntityModels.QuestionType.enum.LongListQuestion:
      case FlatModels.QuestionEntityModels.QuestionType.enum.SingleChoiceQuestion:
      case FlatModels.QuestionEntityModels.QuestionType.enum.FreeTextQuestion:
        total = 1;
        answer = null;
        break;
      default:
        throw new FlatModels.ErrorControlModels.MedsurfError(
          'notification.type_not_supported',
          FlatModels.ErrorControlModels.ErrorLevel.enum.error,
          FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
          "LIBS.FlatStates.TrainingSharedState._createQuestionResult"
        );
        // MULTIPLE_CHOICE = 'MultipleChoiceQuestion',
    }
    return {
      trainingId: slideId,
      questionId: question.id,
      required: question.required || false,
      order: question.order,
      answer,
      showFeedback: false,
      showExplanation: false,
      total,
      empty: total,
      correct: 0,
      wrong: 0,
    };
  }

  //</editor-fold>
}